import type { WebAuthUser } from "@/lib/firebase/types";

const FAVORITES_PREFIX = "sazsoz:favorites:";
const USER_KEY = "sazsoz:web-user";
const LEGACY_USER_KEY = USER_KEY;

function canUseSessionStorage(): boolean {
  return typeof sessionStorage !== "undefined";
}

function canUseLocalStorage(): boolean {
  return typeof localStorage !== "undefined";
}

export function getCachedFavoriteIds(uid: string): string[] | null {
  if (!canUseSessionStorage() || !uid) {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(`${FAVORITES_PREFIX}${uid}`);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return null;
    }
    const out: string[] = [];
    const seen = new Set<string>();
    for (const raw of parsed) {
      let id: string | null = null;
      if (typeof raw === "string") {
        const trimmed = raw.trim();
        id = trimmed.length > 0 ? trimmed : null;
      } else if (
        typeof raw === "number" &&
        Number.isFinite(raw) &&
        raw > 0
      ) {
        id = String(Math.floor(raw));
      }
      if (!id || seen.has(id)) {
        continue;
      }
      seen.add(id);
      out.push(id);
    }
    return out;
  } catch {
    return null;
  }
}

export function setCachedFavoriteIds(uid: string, ids: string[]): void {
  if (!canUseSessionStorage() || !uid) {
    return;
  }
  try {
    sessionStorage.setItem(`${FAVORITES_PREFIX}${uid}`, JSON.stringify(ids));
  } catch {
    /* quota / private mode */
  }
}

export function patchCachedFavoriteIds(
  uid: string,
  patch: (ids: string[]) => string[],
): void {
  const current = getCachedFavoriteIds(uid) ?? [];
  setCachedFavoriteIds(uid, patch(current));
}

function parseCachedWebUser(raw: string | null): WebAuthUser | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as WebAuthUser;
    if (!parsed?.uid) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function getCachedWebUser(): WebAuthUser | null {
  try {
    if (canUseLocalStorage()) {
      const fromLocal = parseCachedWebUser(localStorage.getItem(USER_KEY));
      if (fromLocal) {
        return fromLocal;
      }
    }
    if (canUseSessionStorage()) {
      return parseCachedWebUser(sessionStorage.getItem(LEGACY_USER_KEY));
    }
    return null;
  } catch {
    return null;
  }
}

export function setCachedWebUser(user: WebAuthUser | null): void {
  try {
    if (canUseLocalStorage()) {
      if (!user) {
        localStorage.removeItem(USER_KEY);
      } else {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
      }
    }
    if (canUseSessionStorage()) {
      if (!user) {
        sessionStorage.removeItem(LEGACY_USER_KEY);
      } else {
        sessionStorage.setItem(LEGACY_USER_KEY, JSON.stringify(user));
      }
    }
  } catch {
    /* ignore */
  }
}

export function clearSessionCache(): void {
  try {
    if (canUseLocalStorage()) {
      localStorage.removeItem(USER_KEY);
    }
    if (!canUseSessionStorage()) {
      return;
    }
    sessionStorage.removeItem(LEGACY_USER_KEY);
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(FAVORITES_PREFIX)) {
        keys.push(key);
      }
    }
    for (const key of keys) {
      sessionStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
}

/** Paths that should resolve Firebase auth before showing signed-out UI. */
export function pathNeedsImmediateAuthBoot(pathname: string): boolean {
  return /\/(account|favorites|repertuar)(\/|$)/.test(pathname);
}
